/**
 * Audio / Video call buttons in a channel or DM header (TRUS-668). Staff only,
 * and only when the org has calls on. While a call is active in the channel
 * they become one green "Join call" button.
 *
 * v2: when the org has call notes on (`notes_enabled`) and this is NOT a member
 * DM, a third "Record & take notes" button opens a confirm dialog (recording,
 * consent, transcript + AI summary posted here) and starts with record: true.
 * TRUS-690: in a member DM too when the org records calls with members
 * (`notes_with_members`); the notes are then kept for staff only.
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, ChevronDown, FileText, Loader2, Phone, Video as VideoIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { CallsApiError, type CallMessageMeta, type CallMode } from '@/services/callsApi';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { callStore, useCallStore } from './callStore';
import { maybeAskCallNotificationPermission } from './callNotifications';
import { useCallsApi, useCallsConfig, useLiveCall } from './useCalls';

interface CallHeaderButtonsProps {
  channelId: number;
  channelName?: string;
  /** A member (portal participant) is viewing: never offer Start. */
  viewerIsMember?: boolean;
  /** The newest call message in the channel that says active, if any. */
  activeCall?: CallMessageMeta | null;
  /** A member DM (dm_member_id): recorded only with `notes_with_members` (TRUS-690). */
  isMemberDm?: boolean;
  /**
   * TRUS-691 meetings hook: when set, the call buttons get a split menu
   * ("Start now" / "Schedule meeting"). The dialog lives in meetings/.
   */
  onScheduleMeeting?: () => void;
}

export function CallHeaderButtons({
  channelId,
  channelName,
  viewerIsMember = false,
  activeCall,
  isMemberDm = false,
  onScheduleMeeting,
}: CallHeaderButtonsProps) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const queryClient = useQueryClient();
  const { config } = useCallsConfig();
  const { session } = useCallStore();
  const live = useLiveCall(activeCall?.call_id, config.enabled && !!activeCall);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);

  const start = useMutation({
    mutationFn: ({ mode, record }: { mode: CallMode; record: boolean }) =>
      record ? api.start(channelId, mode, { record: true }) : api.start(channelId, mode),
    onSuccess: (res) => {
      callStore.open({
        callId: res.call.id,
        channelId,
        mode: res.call.mode,
        join: res.join,
        channelName,
      });
      queryClient.invalidateQueries({ queryKey: ['channel-messages', channelId] });
    },
    onError: (err: unknown, vars) => {
      const code = err instanceof CallsApiError ? err.code : null;
      // v2: the channel already has a recording call → consent, then join it.
      if (code === 'consent_required' && err instanceof CallsApiError && err.callId) {
        callStore.open({ callId: err.callId, channelId, mode: vars.mode, channelName, consentNeeded: true });
        return;
      }
      const description =
        code === 'calls_disabled'
          ? t('messaging.calls.disabled', 'Calls are turned off for this organization.')
          : code === 'video_unavailable'
            ? t('messaging.calls.unavailable', 'Calls are not available right now. Try again later.')
            : code === 'members_cannot_start'
              ? t('messaging.calls.membersCannotStart', 'Members can join calls but cannot start them.')
              : code === 'notes_disabled'
                ? t('messaging.calls.notesDisabled', 'Call notes are turned off for this organization.')
                : code === 'recording_not_allowed'
                  ? t('messaging.calls.recordingNotAllowed', 'Calls with members cannot be recorded yet.')
                  : (err as Error)?.message;
      toast.error(t('messaging.calls.startFailed', 'Could not start the call'), { description });
    },
  });

  // A click that starts a call is when we ask to notify about the next one (once).
  const startCall = (vars: { mode: CallMode; record: boolean }) => {
    maybeAskCallNotificationPermission();
    start.mutate(vars);
  };

  if (!config.enabled) return null;

  const liveStatus = live.data?.status ?? activeCall?.status;
  const activeId = activeCall && liveStatus === 'active' ? activeCall.call_id : null;
  if (activeId != null) {
    if (session?.callId === activeId) return null;
    const mode = live.data?.mode ?? activeCall!.mode;
    const consentNeeded = !!live.data?.recording && !live.data?.my_recording_consent;
    return (
      <Button
        type="button"
        size="sm"
        className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
        onClick={() => callStore.open({ callId: activeId, channelId, mode, channelName, consentNeeded })}
        data-testid="header-join-call"
      >
        {mode === 'video' ? <VideoIcon className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
        <span className="hidden sm:inline">{t('messaging.calls.joinCall', 'Join call')}</span>
      </Button>
    );
  }

  if (!config.can_start || viewerIsMember) return null;

  const pending = start.isPending;
  const busyTitle = t('messaging.calls.starting', 'Starting call…');
  const inAnotherCall = !!session;
  const inCallTitle = t('messaging.calls.alreadyInCall', 'You are already in a call');
  const canRecord = config.notes_enabled && (!isMemberDm || config.notes_with_members);
  const startWithNotes = (mode: CallMode) => {
    setRecordDialogOpen(false);
    startCall({ mode, record: true });
  };

  return (
    <>
      <span
        className="inline-flex"
        title={pending ? busyTitle : inAnotherCall ? inCallTitle : t('messaging.calls.startAudio', 'Start an audio call')}
      >
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={pending || inAnotherCall}
        onClick={() => startCall({ mode: 'audio', record: false })}
        aria-label={t('messaging.calls.startAudio', 'Start an audio call')}
        data-testid="start-audio-call"
      >
        {pending && start.variables?.mode === 'audio' && !start.variables.record ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
      </Button>
      </span>
      <span
        className="inline-flex"
        title={pending ? busyTitle : inAnotherCall ? inCallTitle : t('messaging.calls.startVideo', 'Start a video call')}
      >
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={pending || inAnotherCall}
        onClick={() => startCall({ mode: 'video', record: false })}
        aria-label={t('messaging.calls.startVideo', 'Start a video call')}
        data-testid="start-video-call"
      >
        {pending && start.variables?.mode === 'video' && !start.variables.record ? <Loader2 className="h-4 w-4 animate-spin" /> : <VideoIcon className="h-4 w-4" />}
      </Button>
      </span>
      {/* TRUS-691 meetings hook: the split — start now, or schedule for later. */}
      {onScheduleMeeting && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="px-1.5"
              aria-label={t('messaging.meetings.callOptions', 'More call options')}
              title={t('messaging.meetings.callOptions', 'More call options')}
              data-testid="call-split-menu"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={pending || inAnotherCall}
              onSelect={() => startCall({ mode: 'audio', record: false })}
              data-testid="call-split-start-audio"
            >
              <Phone className="mr-2 h-4 w-4" />
              {t('messaging.meetings.startAudioNow', 'Start audio call now')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={pending || inAnotherCall}
              onSelect={() => startCall({ mode: 'video', record: false })}
              data-testid="call-split-start-video"
            >
              <VideoIcon className="mr-2 h-4 w-4" />
              {t('messaging.meetings.startVideoNow', 'Start video call now')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onScheduleMeeting} data-testid="call-split-schedule">
              <CalendarPlus className="mr-2 h-4 w-4" />
              {t('messaging.meetings.scheduleMeeting', 'Schedule meeting…')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {canRecord && (
        <span
          className="inline-flex"
          title={pending ? busyTitle : inAnotherCall ? inCallTitle : t('messaging.calls.recordAndNotes', 'Record & take notes')}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={pending || inAnotherCall}
            onClick={() => setRecordDialogOpen(true)}
            aria-label={t('messaging.calls.recordAndNotes', 'Record & take notes')}
            data-testid="start-recorded-call"
          >
            {pending && start.variables?.record ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          </Button>
        </span>
      )}
      {canRecord && (
        <Dialog open={recordDialogOpen} onOpenChange={setRecordDialogOpen}>
          <DialogContent className="max-w-md" data-testid="record-call-dialog">
            <DialogHeader>
              <DialogTitle>{t('messaging.calls.recordDialogTitle', 'Record this call and take notes?')}</DialogTitle>
              <DialogDescription>
                {isMemberDm
                  ? t(
                      'messaging.calls.recordDialogBodyMemberDm',
                      'The call audio is recorded and transcribed. The transcript and an AI summary are for staff only: they are kept in Calls & notes and on the call card, never shown to the member or posted in this conversation. The recording itself is deleted after transcription.',
                    )
                  : t(
                      'messaging.calls.recordDialogBody',
                      'The call audio is recorded and transcribed. A transcript and an AI summary (action items, decisions) will be posted to this conversation when the call ends. The recording itself is deleted after transcription.',
                    )}
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {t(
                'messaging.calls.recordDialogConsent',
                'Everyone who joins is asked to consent before they can join, and sees a Recording indicator for the whole call. Starting it confirms your own consent.',
              )}
            </p>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={() => setRecordDialogOpen(false)} data-testid="record-call-cancel">
                {t('messaging.calls.cancel', 'Cancel')}
              </Button>
              <Button type="button" variant="secondary" className="gap-1" onClick={() => startWithNotes('audio')} data-testid="record-call-audio">
                <Phone className="h-4 w-4" />
                {t('messaging.calls.startRecordedAudio', 'Start audio call')}
              </Button>
              <Button type="button" className="gap-1" onClick={() => startWithNotes('video')} data-testid="record-call-video">
                <VideoIcon className="h-4 w-4" />
                {t('messaging.calls.startRecordedVideo', 'Start video call')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
